import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { triggerAlert } from "../../utils";
import { AdminCoupons } from "../../utils/APIClient";

/**
 * Search-and-multi-select org picker used by "Eligible orgs" when the
 * coupon audience is Restricted. Debounced backend search on name /
 * billing_email / member email; picked orgs render as removable chips.
 *
 * Value is the list of picked org UUIDs. ``prefilled`` is a list of
 * {id, name, email} objects surfaced by the edit path so we can show
 * chips for orgs the coupon is already restricted to before the admin
 * types anything.
 */
function OrgSearchMultiSelect({ value, onChange, prefilled, error }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [chipsById, setChipsById] = useState(() => {
        const map = {};
        (prefilled || []).forEach((o) => { map[o.id] = o; });
        return map;
    });
    const debounceRef = useRef(null);

    // Fetch as the admin types. Debounced 250ms so we don't hammer the
    // backend on every keystroke.
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const resp = await AdminCoupons.searchOrganizations(query, 20);
                setResults(resp.data?.results || []);
            } catch (e) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, open]);

    const pick = (org) => {
        if (value.includes(org.id)) return;
        setChipsById((m) => ({ ...m, [org.id]: org }));
        onChange([...value, org.id]);
        setQuery("");
    };

    const removeChip = (id) => {
        onChange(value.filter((v) => v !== id));
    };

    return (
        <div>
            <div
                className={`form-control d-flex flex-wrap align-items-center gap-1 p-1 ${error ? "is-invalid" : ""}`}
                style={{ minHeight: 42, cursor: "text" }}
                onClick={() => setOpen(true)}
            >
                {value.map((id) => {
                    const chip = chipsById[id] || { name: id.slice(0, 8), email: "" };
                    return (
                        <span
                            key={id}
                            className="badge bg-primary d-inline-flex align-items-center px-2 py-1"
                            style={{ fontSize: 12, gap: 6 }}
                            title={chip.email || id}
                        >
                            {chip.name}
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                style={{ fontSize: 8 }}
                                onClick={(e) => { e.stopPropagation(); removeChip(id); }}
                                aria-label="Remove"
                            />
                        </span>
                    );
                })}
                <input
                    className="border-0 flex-grow-1"
                    style={{ outline: "none", minWidth: 140, background: "transparent" }}
                    placeholder={value.length === 0 ? "Search by name or email…" : ""}
                    value={query}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {open && (
                <div
                    className="border rounded bg-white shadow-sm mt-1"
                    style={{ maxHeight: 240, overflowY: "auto", position: "relative", zIndex: 5 }}
                >
                    {loading ? (
                        <div className="text-muted small p-2">Searching…</div>
                    ) : results.length === 0 ? (
                        <div className="text-muted small p-2">
                            {query ? "No matches." : "Type to search organizations."}
                        </div>
                    ) : (
                        results.map((org) => {
                            const picked = value.includes(org.id);
                            return (
                                <div
                                    key={org.id}
                                    className={`d-flex justify-content-between align-items-center px-2 py-2 ${picked ? "text-muted" : ""}`}
                                    style={{
                                        cursor: picked ? "default" : "pointer",
                                        borderBottom: "1px solid #f1f3f5",
                                    }}
                                    onClick={() => !picked && pick(org)}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{org.name}</div>
                                        <div className="small text-muted">{org.email || "—"}</div>
                                    </div>
                                    {picked ? <span className="small">✓ selected</span> : <span className="small text-primary">Add</span>}
                                </div>
                            );
                        })
                    )}
                    <div className="p-2 border-top bg-light small text-end">
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={() => setOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Create / edit dialog for a Coupon row. Same component handles both — when
 * ``coupon`` is null we POST; when it's an object we PATCH.
 *
 * Fields conditionally show/hide based on discount_type + audience so the
 * admin can't accidentally set incompatible combos. The backend serializer
 * enforces the same rules so this is UX polish, not a security boundary.
 */

const DISCOUNT_TYPES = [
    { value: "percentage", label: "Percentage (%)" },
    { value: "fixed_amount", label: "Fixed amount ($)" },
    { value: "complimentary", label: "Complimentary (100% off)" },
];

const CATEGORY_OPTIONS = [
    { value: "avatar", label: "Avatar" },
    { value: "calling_v2", label: "Calling" },
    { value: "workflow", label: "Workflow" },
];

const AUDIENCE_OPTIONS = [
    { value: "public", label: "Public (any customer can use the code)" },
    { value: "restricted", label: "Restricted (only whitelisted orgs)" },
];

const isoDateInput = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toISOString().slice(0, 10);
    } catch {
        return "";
    }
};

const isoDateToApi = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    return new Date(`${yyyyMmDd}T00:00:00Z`).toISOString();
};

const empty = () => ({
    code: "",
    name: "",
    description: "",
    discount_type: "percentage",
    discount_value: "",
    max_discount_amount: "",
    applies_to_categories: [],
    applies_to_plans: [],
    audience: "public",
    eligible_orgs: [],
    max_redemptions_total: "",
    max_redemptions_per_org: "",
    valid_from: "",
    valid_until: "",
    is_active: true,
});

export default function CouponDialog({ coupon, onClose, onSaved }) {
    const isEdit = !!coupon;
    const [form, setForm] = useState(empty());
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // When editing a Restricted coupon, resolve the existing eligible_orgs
    // UUIDs to {id, name, email} so the multi-select renders chips with
    // human names instead of raw UUIDs.
    const [prefilledOrgs, setPrefilledOrgs] = useState([]);

    useEffect(() => {
        if (coupon) {
            setForm({
                code: coupon.code || "",
                name: coupon.name || "",
                description: coupon.description || "",
                discount_type: coupon.discount_type || "percentage",
                discount_value: coupon.discount_value ?? "",
                max_discount_amount: coupon.max_discount_amount ?? "",
                applies_to_categories: coupon.applies_to_categories || [],
                applies_to_plans: coupon.applies_to_plans || [],
                audience: coupon.audience || "public",
                eligible_orgs: coupon.eligible_orgs || [],
                max_redemptions_total: coupon.max_redemptions_total ?? "",
                max_redemptions_per_org: coupon.max_redemptions_per_org ?? "",
                valid_from: isoDateInput(coupon.valid_from),
                valid_until: isoDateInput(coupon.valid_until),
                is_active: !!coupon.is_active,
            });
        } else {
            setForm(empty());
        }
        setErrors({});

        // Prefill chip labels for restricted-audience edits — one search per
        // existing eligible_org resolves name + email so chips aren't UUIDs.
        (async () => {
            const ids = coupon?.eligible_orgs || [];
            if (!ids.length) {
                setPrefilledOrgs([]);
                return;
            }
            try {
                const resp = await AdminCoupons.searchOrganizations("", 50);
                const found = (resp.data?.results || []).filter((o) => ids.includes(o.id));
                const missing = ids.filter((id) => !found.find((o) => o.id === id));
                // Missing orgs (paginated beyond the first 50) still get a
                // chip so the admin doesn't accidentally drop them.
                setPrefilledOrgs([
                    ...found,
                    ...missing.map((id) => ({ id, name: id.slice(0, 8) + "…", email: "" })),
                ]);
            } catch (e) {
                setPrefilledOrgs(ids.map((id) => ({ id, name: id.slice(0, 8) + "…", email: "" })));
            }
        })();
    }, [coupon]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleCategory = (cat) => {
        setForm((f) => {
            const cur = new Set(f.applies_to_categories || []);
            if (cur.has(cat)) cur.delete(cat);
            else cur.add(cat);
            return { ...f, applies_to_categories: Array.from(cur) };
        });
    };

    const showPercentageFields = form.discount_type === "percentage";
    const showFixedAmountFields = form.discount_type === "fixed_amount";
    const showValueField = showPercentageFields || showFixedAmountFields;
    const showRestrictedOrgs = form.audience === "restricted";

    const validate = () => {
        const e = {};
        if (!form.code.trim()) e.code = "Code is required.";
        if (!form.name.trim()) e.name = "Name is required.";
        if (showPercentageFields) {
            const n = Number(form.discount_value);
            if (!(n > 0 && n <= 100)) e.discount_value = "Percentage must be > 0 and ≤ 100.";
        } else if (showFixedAmountFields) {
            const n = Number(form.discount_value);
            if (!(n > 0)) e.discount_value = "Amount must be > 0.";
        }
        if (!form.valid_from) e.valid_from = "Start date required.";
        if (!form.valid_until) e.valid_until = "End date required.";
        if (form.valid_from && form.valid_until && form.valid_from >= form.valid_until) {
            e.valid_until = "End date must be after start date.";
        }
        if (showRestrictedOrgs) {
            // eligible_orgs is an ID-list free-text for MVP; require at least one.
            if (!(form.eligible_orgs && form.eligible_orgs.length > 0)) {
                e.eligible_orgs = "Restricted audience requires at least one org ID.";
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                code: form.code.trim(),
                name: form.name.trim(),
                description: form.description || "",
                discount_type: form.discount_type,
                discount_value: showValueField ? Number(form.discount_value) : null,
                max_discount_amount: (showPercentageFields && form.max_discount_amount !== "")
                    ? Number(form.max_discount_amount) : null,
                applies_to_categories: form.applies_to_categories || [],
                applies_to_plans: form.applies_to_plans || [],
                audience: form.audience,
                eligible_orgs: showRestrictedOrgs ? form.eligible_orgs : [],
                max_redemptions_total: form.max_redemptions_total !== ""
                    ? Number(form.max_redemptions_total) : null,
                max_redemptions_per_org: form.max_redemptions_per_org !== ""
                    ? Number(form.max_redemptions_per_org) : null,
                valid_from: isoDateToApi(form.valid_from),
                valid_until: isoDateToApi(form.valid_until),
                is_active: form.is_active,
            };
            if (isEdit) {
                await AdminCoupons.update(coupon.id, payload);
                triggerAlert("success", "Success", "Coupon updated.");
            } else {
                await AdminCoupons.create(payload);
                triggerAlert("success", "Success", "Coupon created.");
            }
            onSaved && onSaved();
        } catch (e) {
            const serverErrors = e?.response?.data;
            if (serverErrors && typeof serverErrors === "object") {
                setErrors(Object.fromEntries(
                    Object.entries(serverErrors).map(([k, v]) => [
                        k, Array.isArray(v) ? v.join(" ") : String(v),
                    ])
                ));
            }
            triggerAlert(
                "error",
                "Oops...",
                e?.response?.data?.detail || "Failed to save coupon."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={onClose} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? `Edit coupon: ${coupon.code}` : "Create coupon"}</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Code<span className="text-danger">*</span></label>
                            <input
                                className={`form-control ${errors.code ? "is-invalid" : ""}`}
                                value={form.code}
                                onChange={(e) => setField("code", e.target.value.toUpperCase())}
                                disabled={isEdit}
                                placeholder="SAVE15"
                                style={{ fontFamily: "monospace" }}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">Name<span className="text-danger">*</span></label>
                            <input
                                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                value={form.name}
                                onChange={(e) => setField("name", e.target.value)}
                                placeholder="Summer promo — 15% off"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                        <div className="col-12">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-control"
                                rows="2"
                                value={form.description}
                                onChange={(e) => setField("description", e.target.value)}
                                placeholder="Optional. Shown to admins only."
                            />
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">Discount type<span className="text-danger">*</span></label>
                            <select
                                className="form-select"
                                value={form.discount_type}
                                onChange={(e) => setField("discount_type", e.target.value)}
                            >
                                {DISCOUNT_TYPES.map((d) => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                        </div>
                        {showValueField && (
                            <div className="col-md-4">
                                <label className="form-label">
                                    Value ({showPercentageFields ? "%" : "$"})<span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number" step="0.01" min="0"
                                    className={`form-control ${errors.discount_value ? "is-invalid" : ""}`}
                                    value={form.discount_value}
                                    onChange={(e) => setField("discount_value", e.target.value)}
                                />
                                {errors.discount_value && (
                                    <div className="invalid-feedback">{errors.discount_value}</div>
                                )}
                            </div>
                        )}
                        {showPercentageFields && (
                            <div className="col-md-4">
                                <label className="form-label">Max discount ($) — optional</label>
                                <input
                                    type="number" step="0.01" min="0"
                                    className="form-control"
                                    value={form.max_discount_amount}
                                    onChange={(e) => setField("max_discount_amount", e.target.value)}
                                    placeholder="e.g. 10.00"
                                />
                                <div className="form-text">Caps the percentage discount at this dollar amount.</div>
                            </div>
                        )}

                        <div className="col-12">
                            <label className="form-label">Applies to categories</label>
                            <div className="d-flex gap-3 flex-wrap">
                                {CATEGORY_OPTIONS.map((c) => (
                                    <div className="form-check" key={c.value}>
                                        <input
                                            id={`cat-${c.value}`}
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={form.applies_to_categories.includes(c.value)}
                                            onChange={() => toggleCategory(c.value)}
                                        />
                                        <label htmlFor={`cat-${c.value}`} className="form-check-label">
                                            {c.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <div className="form-text">Empty = applies to any category (except phone numbers, which don't accept coupons).</div>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Audience</label>
                            <select
                                className="form-select"
                                value={form.audience}
                                onChange={(e) => setField("audience", e.target.value)}
                            >
                                {AUDIENCE_OPTIONS.map((a) => (
                                    <option key={a.value} value={a.value}>{a.label}</option>
                                ))}
                            </select>
                        </div>
                        {showRestrictedOrgs && (
                            <div className="col-md-6">
                                <label className="form-label">
                                    Eligible organizations<span className="text-danger">*</span>
                                </label>
                                <OrgSearchMultiSelect
                                    value={form.eligible_orgs || []}
                                    onChange={(v) => setField("eligible_orgs", v)}
                                    prefilled={prefilledOrgs}
                                    error={!!errors.eligible_orgs}
                                />
                                {errors.eligible_orgs && (
                                    <div className="text-danger small mt-1">{errors.eligible_orgs}</div>
                                )}
                                <div className="form-text">Search by company name, billing email, or any team member's email. Add multiple.</div>
                            </div>
                        )}

                        <div className="col-md-4">
                            <label className="form-label">Max redemptions total</label>
                            <input
                                type="number" min="1"
                                className="form-control"
                                value={form.max_redemptions_total}
                                onChange={(e) => setField("max_redemptions_total", e.target.value)}
                                placeholder="Leave empty for unlimited"
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Max redemptions per org</label>
                            <input
                                type="number" min="1"
                                className="form-control"
                                value={form.max_redemptions_per_org}
                                onChange={(e) => setField("max_redemptions_per_org", e.target.value)}
                                placeholder="Leave empty for unlimited"
                            />
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                            <div className="form-check">
                                <input
                                    id="is_active"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={form.is_active}
                                    onChange={(e) => setField("is_active", e.target.checked)}
                                />
                                <label htmlFor="is_active" className="form-check-label">Active</label>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Valid from<span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className={`form-control ${errors.valid_from ? "is-invalid" : ""}`}
                                value={form.valid_from}
                                onChange={(e) => setField("valid_from", e.target.value)}
                            />
                            {errors.valid_from && (
                                <div className="invalid-feedback">{errors.valid_from}</div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Valid until<span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className={`form-control ${errors.valid_until ? "is-invalid" : ""}`}
                                value={form.valid_until}
                                onChange={(e) => setField("valid_until", e.target.value)}
                            />
                            {errors.valid_until && (
                                <div className="invalid-feedback">{errors.valid_until}</div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Create coupon"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
